import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import { logout, refreshToken } from "@/services/auth";

import ErrorBoundary from "@/components/ErrorBoundary";
import { NavBar } from "@/components/layout/NavBar";
import { ToastProvider, useToast } from "@/context/ToastContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { ToastContainer } from "@/components/ui/Toast";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages//RegisterPage";
import VerifyEmailPage from "@/pages/VerifyEmailPage";

import ProfilePage from "@/pages/ProfilePage";
import DashboardPage from "@/pages/DashboardPage";
import JobCreatePage from "@/pages/JobCreatePage";
import JobDetailPage from "@/pages/JobDetailPage";

// ── Auth context ──────────────────────────────────────────────────────────────

interface AuthContextValue {
  isAuthenticated: boolean;
  isLoading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue>({
  isAuthenticated: false,
  isLoading: true,
  signOut: () => Promise.resolve(),
});

export function useAuth() {
  return useContext(AuthContext);
}

function AuthProvider({ children }: { children: React.ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // On mount, try to refresh token silently (uses HttpOnly cookie)
    refreshToken()
      .then((ok) => setIsAuthenticated(ok))
      .finally(() => setIsLoading(false));
  }, []);

  const signOut = useCallback(async () => {
    await logout();           // clears httpOnly refresh cookie via POST /api/auth/logout
    setIsAuthenticated(false);
  }, []);

  return (
    <AuthContext.Provider value={{ isAuthenticated, isLoading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

// ── Protected layout (nav bar + outlet) ──────────────────────────────────────

function ProtectedLayout() {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center">
      <p className="text-slate-500 dark:text-slate-400 text-sm">Loading…</p>
    </div>
  );
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-100">
      <NavBar />
      <main className="px-6 py-6">
        <Outlet />
      </main>
    </div>
  );
}

// ── Toast bridge (inside ToastProvider) ──────────────────────────────────────

function ToastBridge() {
  const { toasts, dismiss } = useToast();
  return <ToastContainer toasts={toasts} onDismiss={dismiss} />;
}

// ── App ───────────────────────────────────────────────────────────────────────

export default function App() {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <ThemeProvider>
        <AuthProvider>
          <ToastProvider>
            <ToastBridge />
            <Routes>
              {/* Public */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/verify-email" element={<VerifyEmailPage />} />

              {/* Protected — all share NavBar via ProtectedLayout */}
              <Route element={<ProtectedLayout />}>
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/profile" element={<ProfilePage />} />
                <Route path="/jobs/new" element={<JobCreatePage />} />
                <Route path="/jobs/:jobId" element={<JobDetailPage />} />
              </Route>

              {/* Default */}
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </ToastProvider>
        </AuthProvider>
        </ThemeProvider>
      </ErrorBoundary>
    </BrowserRouter>
  );
}
